import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { log } from '@/lib/logger';
import type { AvatarCacheStatus } from '@monthly-data-collection/shared';

// Cloud Function references
const cacheUserAvatarFunction = httpsCallable(functions, 'cacheUserAvatar');
const deleteUserAvatarFunction = httpsCallable(functions, 'deleteUserAvatar');

interface CacheAvatarResponse {
  success: boolean;
  cachedUrl: string;
}

interface DeleteAvatarResponse {
  success: boolean;
}

interface AvatarCacheInfo {
  needsCaching: boolean;
  cacheStatus?: AvatarCacheStatus;
  cacheAge?: number; // Age in hours
  lastCached?: Date;
}

/**
 * Client-side service for managing avatar caching operations
 * Provides methods to cache, delete, and check avatar cache status
 */
export class AvatarCacheService {
  /**
   * Cache a user's avatar by calling the Cloud Function
   * This is an asynchronous operation that doesn't block the UI
   */
  static async cacheUserAvatar(employeeId: string, avatarUrl: string): Promise<string> {
    try {
      log.debug(`[AVATAR-CACHE] Caching avatar for employee ${employeeId}`);
      
      const result = await cacheUserAvatarFunction({
        employeeId,
        googleAvatarUrl: avatarUrl // Keep parameter name for backward compatibility with cloud function
      });
      
      const response = result.data as CacheAvatarResponse;
      
      if (!response.success) {
        throw new Error('Avatar caching failed');
      }
      
      log.debug(`[AVATAR-CACHE] Successfully cached avatar for employee ${employeeId}`);
      return response.cachedUrl;
      
    } catch (error) {
      // Handle specific error types
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
        log.error(`[AVATAR-CACHE] Permission denied for employee ${employeeId} - check Firebase Storage rules`);
        throw new Error('Permission denied - unable to cache avatar');
      }
      
      log.error(`[AVATAR-CACHE] Failed to cache avatar for employee ${employeeId}:`, error);
      throw new Error(`Failed to cache avatar: ${errorMessage}`);
    }
  }

  /**
   * Delete a user's cached avatar
   */
  static async deleteUserAvatar(employeeId: string): Promise<void> {
    try {
      log.debug(`[AVATAR-CACHE] Deleting cached avatar for employee ${employeeId}`);
      
      const result = await deleteUserAvatarFunction({
        employeeId
      });
      
      const response = result.data as DeleteAvatarResponse;
      
      if (!response.success) {
        throw new Error('Avatar deletion failed');
      }
      
      log.debug(`[AVATAR-CACHE] Successfully deleted cached avatar for employee ${employeeId}`);
      
    } catch (error) {
      log.error(`[AVATAR-CACHE] Failed to delete cached avatar for employee ${employeeId}:`, error);
      throw new Error(`Failed to delete avatar: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Helper function to convert Firestore Timestamp to Date
   */
  private static convertTimestamp(timestamp: any): Date | undefined {
    if (!timestamp) return undefined;
    
    // If it's already a Date object, return it
    if (timestamp instanceof Date) return timestamp;
    
    // If it's a Firestore Timestamp, convert it
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // If it's a timestamp object with seconds/nanoseconds
    if (timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    }
    
    // Try to parse as date string
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    
    return undefined;
  }

  /**
   * Check if an avatar needs caching based on cache status and age
   */
  static checkCacheStatus(
    avatar?: string,
    cachedAvatarUrl?: string,
    cacheStatus?: AvatarCacheStatus,
    cacheTimestamp?: Date | any,
    maxAgeHours: number = 24
  ): AvatarCacheInfo {
    // No original avatar to cache
    if (!avatar) {
      return { needsCaching: false };
    }

    // No cached version exists or cache failed
    if (!cachedAvatarUrl || cacheStatus !== 'cached') {
      return {
        needsCaching: true,
        cacheStatus: cacheStatus || undefined
      };
    }

    // Convert timestamp to Date object
    const convertedTimestamp = this.convertTimestamp(cacheTimestamp);
    
    // Check cache age
    if (!convertedTimestamp) {
      return {
        needsCaching: true,
        cacheStatus
      };
    }

    const now = new Date();
    const cacheAge = (now.getTime() - convertedTimestamp.getTime()) / (1000 * 60 * 60); // Hours
    const needsCaching = cacheAge > maxAgeHours;

    return {
      needsCaching,
      cacheStatus,
      cacheAge,
      lastCached: convertedTimestamp
    };
  }

  /**
   * Get the best available avatar URL (cached only, no fallback to original)
   */
  static getBestAvatarUrl(
    cachedAvatarUrl?: string,
    cacheStatus?: AvatarCacheStatus
  ): string | undefined {
    // Only return cached avatar if available and status is 'cached'
    if (cachedAvatarUrl && cacheStatus === 'cached') {
      return cachedAvatarUrl;
    }

    // No fallback - return undefined if cached version not available
    return undefined;
  }

  /**
   * Get thumbnail URL if available
   */
  static getThumbnailUrl(
    cachedThumbnailUrl?: string,
    cacheStatus?: AvatarCacheStatus
  ): string | undefined {
    // Return thumbnail if available and status is 'cached'
    if (cachedThumbnailUrl && cacheStatus === 'cached') {
      return cachedThumbnailUrl;
    }

    return undefined;
  }

  /**
   * Safely cache avatar without blocking the UI
   * This method handles errors gracefully and logs them
   */
  static async safeCacheAvatar(employeeId: string, avatarUrl: string): Promise<void> {
    try {
      await this.cacheUserAvatar(employeeId, avatarUrl);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // Different handling based on error type
      if (errorMessage.includes('Permission denied')) {
        log.error(`[AVATAR-CACHE] Permission error for employee ${employeeId} - check Firebase configuration`);
      } else {
        log.warn(`[AVATAR-CACHE] Background avatar caching failed for employee ${employeeId}:`, error);
      }
    }
  }

  /**
   * Cache avatar if needed based on cache status
   * This is the main method to be called during authentication
   */
  static async cacheAvatarIfNeeded(
    employeeId: string,
    avatar?: string,
    cachedAvatarUrl?: string,
    cacheStatus?: AvatarCacheStatus,
    cacheTimestamp?: Date | any,
    maxAgeHours: number = 24
  ): Promise<void> {
    const cacheInfo = this.checkCacheStatus(
      avatar,
      cachedAvatarUrl,
      cacheStatus,
      cacheTimestamp,
      maxAgeHours
    );

    if (cacheInfo.needsCaching && avatar) {
      log.debug(`[AVATAR-CACHE] Avatar needs caching for employee ${employeeId}:`, {
        hasOriginal: !!avatar,
        hasCached: !!cachedAvatarUrl,
        cacheStatus,
        cacheAge: cacheInfo.cacheAge,
        lastCached: cacheInfo.lastCached
      });

      // Cache asynchronously without blocking
      this.safeCacheAvatar(employeeId, avatar);
    } else {
      log.debug(`[AVATAR-CACHE] Avatar cache is up to date for employee ${employeeId}`, {
        cacheAge: cacheInfo.cacheAge,
        lastCached: cacheInfo.lastCached
      });
    }
  }
}

